import * as React from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogContent';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';
import { Virtual } from 'swiper/modules';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';

import { useGetConsumerDetailedQuery, GetConsumerDetailedQuery } from '@graphql';
import Header from './header';
import Home from './home';
import Memories from './memories';
import Friends from './friends';
import ConsumerSkeleton from './ConsumerSkeleton';

interface ConsumerDialogProps {
  handleBack: (toRoot: boolean) => void;
}

function ConsumerDialog(props: Readonly<ConsumerDialogProps>) {
  const { handleBack } = props;
  const [activeTab, setActiveTab] = React.useState(0);
  const [consumer, setConsummer] = React.useState<GetConsumerDetailedQuery['consumer'] | null>(null);
  const swiperRef = React.useRef<SwiperClass>();
  const [searchParams] = useSearchParams();
  const { memberSlug = null } = useParams();
  const { t } = useTranslation('common');

  const { data, loading, called } = useGetConsumerDetailedQuery({
    // if no slug nor memberSlug, skip query as we won't be able to fetch any consumer
    skip: !searchParams.get('c') && !memberSlug,
    variables: { slug: searchParams.get('c') ?? memberSlug ?? '' },
    onCompleted: ({ consumer }) => {
      setConsummer(consumer);
    },
  });

  const handleTabChange = (event: React.SyntheticEvent, newTab: number) => {
    setActiveTab(newTab);
    swiperRef.current?.slideTo(newTab);
  };

  if (loading) {
    return <ConsumerSkeleton />;
  }

  if (called && !data?.consumer) {
    return (
      <Stack p={5} spacing={3} justifyContent="center" alignItems="center">
        <Typography variant="h6">{t('error.Something went wrong')}</Typography>
        <Button variant="contained" color="primary" onClick={props.handleBack}>
          {t('back', { ns: 'cta' })}
        </Button>
      </Stack>
    );
  }

  if (consumer) {
    return (
      <Stack sx={{ position: 'relative' }}>
        <Header
          consumer={consumer}
          handleBack={handleBack}
          activeTab={activeTab}
          handleTabChange={handleTabChange}
        />
        <DialogContent
          sx={{
            p: 0,
            width: '100%',
            minHeight: '30vh',
          }}
        >
          <Stack spacing={5}>
            <Swiper
              modules={[Virtual]}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              slidesPerView={1}
              style={{ marginTop: 0 }}
              initialSlide={0}
              virtual
            >
              <SwiperSlide virtualIndex={0}>{<Home consumer={consumer} />}</SwiperSlide>
              <SwiperSlide virtualIndex={1}>{<Memories consumer={consumer} />}</SwiperSlide>
              <SwiperSlide virtualIndex={2}>{<Friends consumer={consumer} />}</SwiperSlide>
            </Swiper>
          </Stack>
        </DialogContent>
      </Stack>
    );
  }

  return null;
}

export default React.memo(ConsumerDialog);
